import {
  ApiBuildStoryGame,
  ApiDeleteStoryGameDialog,
  ApiDeleteStoryGameDialogAnimation,
  ApiDeleteStoryGameDialogOption,
  ApiDeleteStoryGameDialogPortrait,
  ApiDeleteStoryGameMedia,
  ApiDeleteStoryGameStoryNode,
  ApiGetStoryGame,
  ApiGetStoryGameDialog,
  ApiGetStoryGameDialogItem,
  ApiGetStoryGameDialogPageByCharacterId,
  ApiGetStoryGameStoryNode,
  ApiPostStoryGameDialog,
  ApiPostStoryGameDialogAnimation,
  ApiPostStoryGameDialogOption,
  ApiPostStoryGameDialogPortrait,
  ApiPostStoryGamePublish,
  ApiPostStoryGameStoryNode,
  ApiPostStoryGameStoryNodeClone,
  ApiPostStoryGameStoryNodeSort,
  ApiPutStoryGame,
  ApiPutStoryGameDialog,
  ApiPutStoryGameDialogAnimation,
  ApiPutStoryGameDialogOption,
  ApiPutStoryGameDialogPortrait,
  ApiPutStoryGameDialogSort,
  ApiPutStoryGameMediaSort,
  ApiPutStoryGameStoryNode,
} from "@/apis/story-game";
import { defineStore } from "pinia";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { StorageGet } from "@/tools/localStorage";
import { useMate } from "./mate";
import api from "@/modules/api";
import { useMoment } from "./moment";
import { useStoryBook } from "./story-book";
import i18n from "@/locales";
import { RouteLocationRaw } from "vue-router";

// const storyBookStore = useStoryBook();

export const GAL_GAME_URL_PREFIX = `${
  // @ts-ignore
  import.meta.env.VITE_GAL_GAME_URL
}/webgal/`;

export const useStoryGameEditor = defineStore("story-game-editor", {
  state: () => ({
    tabType: "base",
    scrollTopCache: 0,
    scrollTopDialogCache: 0,

    mateToneId: 0,

    storyGameId: 0,
    storyGameUUID: "",
    showJumpSettingModal: false, // 跳转设置对话框
    showJumpListModal: false, // 选择跳转的对话/节点
    showSelectMateModal: false, // 选择参与Mate
    showPlayerEditModal: false, // 编辑玩家信息
    showImgUploadModal: false, // 上传图片

    mediaType: "img" as "img" | "video", // 上传类型 img: 图片 video: 视频
    mediaIdx: -1,
    uploadType: 0, // 上传类型 0: 玩家扮演 1: 游戏封面图 2: 截图
    activeMediaId: undefined as number | undefined,

    showInputModal: false, // 输入框
    modalInputType: "",
    modalInputContent: "", // 输入框内容

    showScoreSliderModal: false, // 评分
    modalScore: 0, // 评分

    showGenerate: false, // 生成弹框
    showGenerateLoadingModal: false, // 生成loading
    generateContent: "",
    isGenerating: false,
    generateTarget: "" as StoryGameParams | "node",
    generateChecked: ["type"] as StoryGameParams[],
    activeCharacterId: 0 as number | undefined, // 当前选中的角色ID
    showSelectCharacterToneModal: false, // 选择角色音色弹框

    //
    showBgmModal: false, // 背景音乐
    bgmType: 0, // 0: 背景音乐 1: 节点音乐
    currentBgmId: 0,

    showPublishModal: false, // 发布弹框

    isParent: false, // 是否是父节点
    activeNode: {} as StoryGameNode, // 当前选中的节点
    activeDialog: {} as StoryGameDialog, // 当前选中的对话
    activeDialogOption: {} as StoryGameDialogOption, // 当前选中的对话选项
    lastDialogIdEditedPerformance: 0, // 上次进入演出编辑器的对话ID

    connectionType: "",

    tmpMedia: { id: 0, url: "" }, // 临时url

    saveLoading: false,

    isInitStoryGame: false,
    isInitNode: false,
    isInitDialog: false,

    nodeMap: {} as Record<number, StoryGameNode>,
    dialogMap: {} as Record<number, StoryGameDialog>,
    optionMap: {} as Record<number, StoryGameDialogOption>,

    nodes: [] as number[],
    dialogs: [] as number[],
    options: [] as number[],

    endingList: [] as number[],
    plotList: [] as number[],
    beginningNode: 0,

    form: {
      id: 0,
      name: "",
      description: "",
      coverMediaId: 0,
      bgmMediaId: 0,
      type: 1,
      characterzz: [] as StoryGameCharacter[],
      player: {
        name: "",
        introduction: "",
        avatarMediaId: 0,
      },
    } as StoryGame,

    characterIdMap: {} as Record<number, StoryGameCharacter>,
    mediaMap: {} as Record<number, Media>,
    mediaList: [] as number[],

    dialogList: [] as number[],
    activeCheckedJumpDialogId: 0 as number | "node", // 当前选中的跳转对话ID
    activeCheckedJumpNodeId: 0 as number, // 当前选中的跳转节点ID
    dialogShowTab: 0,
    dialogShowList: [] as number[],

    activeVersion: {
      versionNumber: ["1", "0", "0"],
      description: "",
    },
    latestVersion: null as StoryGameVersion | null,
    versionzz: [] as StoryGameVersion[],

    showCloneNodeModal: false,
    activeCloneNodeId: 0,

    showCharacterAllDialogModal: false,
    characterDialogList: [] as StoryGameDialog[],
    activeStoryNodeId: undefined as number | undefined,
    needReloadCharacterDialogList: false,
  }),
  actions: {
    async addNode(node: StoryGameNode) {
      this.saveLoading = true;
      const { item } = await ApiPostStoryGameStoryNode(
        this.storyGameId,
        node.type
      );
      this.saveLoading = false;
      node.id = item.id;
      this.nodeMap[node.id!] = item;
      this.nodes.push(node.id!);
      if (node.type === 1) {
        this.beginningNode = node.id!;
      }
      if (node.type === 2) {
        this.plotList.push(node.id!);
      }
      if (node.type === 3) {
        this.endingList.push(node.id!);
      }
    },
    async deleteNode(node: StoryGameNode) {
      const { id, type } = node;
      this.saveLoading = true;
      await ApiDeleteStoryGameStoryNode(id!);
      this.saveLoading = false;
      delete this.nodeMap[id!];
      const index = this.nodes.indexOf(id!);
      this.nodes.splice(index, 1);
      if (type === 2) this.plotList.splice(this.plotList.indexOf(id!), 1);
      if (type === 3) this.endingList.splice(this.endingList.indexOf(id!), 1);
    },
    async cloneNode(targetNodeId: number) {
      const loading = showLoadingToast({
        message: i18n.global.t("Message.nodeCloning"),
      });
      try {
        const { item } = await ApiPostStoryGameStoryNodeClone(
          this.activeCloneNodeId
        );
        this.nodeMap[item.id!] = item;
        this.nodes.push(item.id!);
        const { type, id } = this.nodeMap[targetNodeId];
        if (type === 1) this.plotList.unshift(item.id!);
        if (type === 2)
          this.plotList.splice(this.plotList.indexOf(id!) + 1, 0, item.id!);
        if (type === 3) {
          item.type = 3;
          await this.saveNode(item);
          this.endingList.splice(this.endingList.indexOf(id!) + 1, 0, item.id!);
        }

        await this.sortNode();
        loading.close();
        showSuccessToast(i18n.global.t("Message.nodeCloneSuccessfully"));
      } catch (e) {
        loading.close();
        showFailToast(i18n.global.t("Message.cloningFailed"));
      }
    },
    async sortNode() {
      const { beginningNode, plotList, endingList, storyGameId } = this;
      this.saveLoading = true;
      this.nodes = [beginningNode, ...plotList, ...endingList];
      await ApiPostStoryGameStoryNodeSort({
        storyGameId,
        storyNodeIdzz: this.nodes,
      });
      this.saveLoading = false;
    },
    async sortDialog() {
      const { activeNode } = this;
      this.saveLoading = true;
      await ApiPutStoryGameDialogSort({
        storyNodeId: activeNode.id!,
        dialogIdzz: [...this.dialogList],
      });
      this.saveLoading = false;
    },
    async sortMedia() {
      const { storyGameId } = this;
      this.saveLoading = true;
      await ApiPutStoryGameMediaSort({
        storyGameId,
        mediaIdzz: [...this.mediaList],
      });
      this.saveLoading = false;
    },
    async initNode(storyGameId: number, nodeId?: number) {
      if (this.isInitNode) return;
      this.isInitNode = true;
      this.storyGameId = storyGameId;
      const { itemzz } = await ApiGetStoryGameStoryNode(storyGameId);
      for (const item of itemzz) {
        this.nodeMap[item.id!] = item;
        this.nodes.push(item.id!);
      }
      if (nodeId) this.activeNode = this.nodeMap[nodeId];
    },
    async initStoryGame(uuid: string) {
      if (this.isInitStoryGame) return;
      this.isInitStoryGame = true;
      const { item } = await ApiGetStoryGame(uuid);
      const {
        id,
        name,
        type,
        description,
        coverMediaId,
        coverMedia,
        characterzz,
        bgmMediaId,
        bgmMedia,
        latestVersion,
        versionzz,
        mediazz,
      } = item;
      this.storyGameId = id;
      this.storyGameUUID = uuid;
      const form: StoryGame = {
        id,
        type,
        name,
        description,
        coverMediaId,
        coverMedia,
        bgmMediaId,
        bgmMedia,
        characterzz: [] as StoryGameCharacter[],
      } as StoryGame;

      // 避免重复添加
      if (!useStoryBook().form.characterzz)
        useStoryBook().form.characterzz = [];

      for (const character of characterzz) {
        const {
          id,
          mateId,
          name,
          role,
          introduction,
          avatarMedia,
          avatarMediaId,
          mateToneId,
        } = character;
        if (mateId)
          useStoryBook().form.characterzz!.push({ mateId } as StoryCharacter);
        this.characterIdMap[id] = {
          id,
          mateId,
          name,
          avatarMediaId,
          avatarMedia,
          role,
          mateToneId,
        };
        if (role === 1 || role === 2) {
          // 主角
          form.characterzz.push({
            id,
            mateId,
            introduction,
            role,
            mateToneId,
            isOpen: false,
          });
        } else if (role === 3) {
          // 玩家
          form.player = {
            id,
            name,
            introduction,
            avatarMediaId,
            avatarMedia,
            mateToneId,
            role,
          };
        } else if (role === 4) {
          // 旁白
          form.narrator = {
            id,
            role,
            mateToneId,
          };
        }
      }
      this.form = form;
    },
    async addDialog(role: StoryGameCharacterRole, text?: string, idx?: number) {
      if (this.saveLoading)
        return showFailToast(
          i18n.global.t("Message.addingPleaseTryAgainLater")
        );
      this.saveLoading = true;
      const { id } = this.activeNode;
      try {
        const { item } = await ApiPostStoryGameDialog({
          storyNodeId: id!,
          characterId: this.form.narrator.id,
          text,
          sceneMediaId: null,
          mateToneId: this.mateToneId,
        });
        this.dialogMap[item.id!] = item;
        item.isOpen = false;
        // 避免重复添加会话
        if (!this.dialogList.includes(item.id!)) {
          this.nodeMap[id!].dialogzzCount!++;
          if (idx === undefined) this.dialogList.push(item.id!);
          else {
            this.dialogList.splice(idx, 0, item.id!);
            await this.sortDialog();
          }
          if (this.dialogShowTab === 0 || this.dialogShowTab === role)
            this.dialogShowList.push(item.id!);
        }
      } catch (e) {
        console.log(e);
        showFailToast(
          i18n.global.t("Message.conversationAdditionFailed") + (e || "")
        );
      }
      this.saveLoading = false;
    },
    getDefaultDialog(characterId: number) {
      const prevDialog =
        this.lastDialogIdEditedPerformance &&
        this.dialogMap[this.lastDialogIdEditedPerformance]
          ? this.dialogMap[this.lastDialogIdEditedPerformance]
          : null;
      let sceneMediaId: number | null = prevDialog?.sceneMediaId ?? null;
      let portraitzz: Pick<StoryGameDialogPortrait, "mediaId" | "mediaInfo">[] =
        prevDialog?.portraitzz ?? [];
      if (!sceneMediaId && !portraitzz.length) {
        Object.keys(this.dialogMap).some((key) => {
          const dialog = this.dialogMap[Number(key)];
          if (
            !sceneMediaId &&
            dialog.sceneMediaId &&
            !portraitzz.length &&
            dialog.portraitzz.length
          ) {
            if (dialog.sceneMediaId) sceneMediaId = dialog.sceneMediaId;
            if (dialog.portraitzz) portraitzz = dialog.portraitzz;
            if (sceneMediaId || portraitzz.length) return true;
          }
        });
      }
      portraitzz = portraitzz.map((v) => ({
        mediaId: v.mediaId,
        mediaInfo: v.mediaInfo,
      }));
      const { mateToneId } = this.characterIdMap[characterId];
      return { sceneMediaId, mateToneId, portraitzz };
    },

    async resetGame(item: { id: number; uuid: string; storyNodeId: number }) {
      this.$reset();
      const loading = showLoadingToast({
        message: i18n.global.t("Message.loading"),
      });
      await Promise.all([
        this.initStoryGame(item.uuid),
        this.initNode(item.id, item.storyNodeId),
        this.initDialog(item.storyNodeId),
      ]);
      loading.close();
    },

    deleteDialog(dialog: StoryGameDialog) {
      showConfirmDialog({
        title: i18n.global.t("ConfirmModal.deleteDialogue"),
        message: i18n.global.t("ConfirmModal.deleteDialogueText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          this.saveLoading = true;
          await ApiDeleteStoryGameDialog(dialog.id!);
          showSuccessToast(i18n.global.t("Message.deleteSuccessful"));
          this.saveLoading = false;
          this.nodeMap[dialog.storyNodeId!].dialogzzCount!--;
          const index = this.dialogList.indexOf(dialog.id!);
          this.dialogList.splice(index, 1);
          delete this.dialogMap[dialog.id!];
          if (
            this.dialogShowTab === 0 ||
            this.dialogShowTab === dialog.character.role
          ) {
            const idx = this.dialogShowList.indexOf(dialog.id!);
            this.dialogShowList.splice(idx, 1);
          }
        })
        .catch(() => {});
    },

    async initDialog(nodeId: number, force = false) {
      if (this.isInitDialog && !force) return;
      this.isInitDialog = true;
      // this.activeNode = this.nodeMap[nodeId];
      const res = await ApiGetStoryGameDialog(nodeId);
      this.dialogList = res.itemzz.map((dialog: StoryGameDialog) => {
        return (this.dialogMap[dialog.id!] = { ...dialog }), dialog.id!;
      });
      this.dialogShowList = [...this.dialogList];
    },

    async addDialogOption(dialogId: number) {
      this.saveLoading = true;
      const idx = this.dialogList.findIndex((v) => v === dialogId);
      if (idx === -1) return;
      this.dialogMap[this.dialogList[idx]].isOpen = true;
      const { item } = await ApiPostStoryGameDialogOption({
        dialogId,
        text: "",
      });
      this.saveLoading = false;
      this.optionMap[item.id!] = item;
      this.dialogMap[this.dialogList[idx]].optionzz.push(item);
    },

    async updateDialogImg(
      dialogId: number,
      param: Partial<
        Pick<
          StoryGameDialog,
          "sceneMediaId" | "sceneMediaInfo" | "previewMediaId"
        >
      >
    ) {
      const dialog = this.dialogMap[dialogId];
      const { item } = await ApiPutStoryGameDialog(dialogId, param);
      dialog.sceneMediaId = item.sceneMediaId;
      dialog.sceneMediaInfo = item.sceneMediaInfo;
      dialog.previewMediaId = item.previewMediaId;
      dialog.sceneMedia = item.sceneMedia;
      dialog.previewMedia = item.previewMedia;
    },

    async saveDialog(dialog?: StoryGameDialog) {
      if (!dialog) dialog = this.activeDialog;
      this.saveLoading = true;
      const res = await ApiPutStoryGameDialog(dialog.id!, dialog);
      this.saveLoading = false;
      return res;
    },

    getDialogImgUrl() {
      if (!this.activeDialog.id)
        this.activeDialog = StorageGet("activeDialog") as StoryGameDialog;
      return this.activeDialog.sceneMedia?.url;
    },

    async deleteDialogOption(option: StoryGameDialogOption) {
      showConfirmDialog({
        title: i18n.global.t("ConfirmModal.deleteOption"),
        message: i18n.global.t("ConfirmModal.deleteOptionText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          const { id } = option;
          this.saveLoading = true;
          await ApiDeleteStoryGameDialogOption(id!);
          this.saveLoading = false;
          const idx = this.dialogList.findIndex((v) =>
            this.dialogMap[v].optionzz.some((vv) => vv.id === id)
          );
          if (idx === -1) return;
          const optionIdx = this.dialogMap[
            this.dialogList[idx]
          ].optionzz.findIndex((v) => v.id === id);
          this.dialogMap[this.dialogList[idx]].optionzz.splice(optionIdx, 1);
        })
        .catch(() => {});
    },

    async saveDialogOption(option?: StoryGameDialogOption) {
      if (!option) option = this.activeDialogOption;
      this.saveLoading = true;
      const res = await ApiPutStoryGameDialogOption(option.id!, option);
      this.saveLoading = false;
      return res;
    },

    filterOptionText(dialog: StoryGameDialog) {
      const idx = this.dialogList.findIndex((item) => item === dialog.id);
      if (idx === -1) {
        const { type } = this.nodeMap[dialog.storyNodeId];
        if (type === 1) return `${i18n.global.t("game.beginning")}：`;
        if (type === 2) {
          const idx = this.plotList.findIndex(
            (item) => item === dialog.storyNodeId
          );
          return `${i18n.global.t("game.plotNode")}${idx + 1}：`;
        }
        if (type === 3) {
          const idx = this.endingList.findIndex(
            (item) => item === dialog.storyNodeId
          );
          return `${i18n.global.t("game.ending.title")}${idx + 1}：`;
        }
        return `${i18n.global.t("game.nodeId")}:` + dialog.storyNodeId;
      }
      return `#${idx + 1}`;
    },
    async saveBase(form?: StoryGame) {
      this.saveLoading = true;
      if (!form || !form.id) form = this.form;
      const { item } = await ApiPutStoryGame(form.id!, form);
      // const home = useStoryGameHome();
      // const origin = home.gameMap[form.id!];
      // if (origin) home.gameMap[form.id!] = { ...origin, ...form };
      this.saveLoading = false;
      return item;
    },
    filterNextDialogText(dialog: StoryGameDialog) {
      const { id, text } = dialog;
      return this.dialogMap[id!]?.text || text;
    },

    async saveNode(node?: StoryGameNode) {
      this.saveLoading = true;
      if (!node) node = this.activeNode;
      await ApiPutStoryGameStoryNode(node.id!, node);
      this.saveLoading = false;
    },

    async setDialogPortrait(
      dialogId: number,
      portraits: {
        id?: number;
        mediaId: number;
        mediaInfo: MediaTransform;
      }[]
    ) {
      const dialog = this.dialogMap[dialogId];
      const oldPortraitIds = dialog.portraitzz.map((v) => v.id!);
      const toAddList = portraits.filter((p) => !p.id);
      const toDeleteIds = oldPortraitIds.filter(
        (id) => !portraits.some((p) => p.id === id)
      );
      const toUpdateList = portraits.filter((p) => p.id);
      const promises: Promise<any>[] = [];
      for (const portrait of toAddList) {
        promises.push(
          ApiPostStoryGameDialogPortrait({
            dialogId,
            mediaId: portrait.mediaId,
            mediaInfo: portrait.mediaInfo,
          })
        );
      }
      for (const portrait of toUpdateList) {
        promises.push(
          ApiPutStoryGameDialogPortrait(portrait.id!, {
            mediaId: portrait.mediaId,
            mediaInfo: portrait.mediaInfo,
          })
        );
      }
      for (const id of toDeleteIds) {
        promises.push(ApiDeleteStoryGameDialogPortrait(id));
      }
      await Promise.all(promises);
      const { item } = await ApiGetStoryGameDialogItem(dialogId);
      dialog.portraitzz = item.portraitzz;
    },
    async setDialogAnimation(
      dialogId: number,
      animations: {
        id?: number;
        animationId: number;
        mediaInfo: MediaTransform;
      }[]
    ) {
      const dialog = this.dialogMap[dialogId];
      const oldAnimationIds = dialog.animationzz.map((v) => v.id!);
      const toAddList = animations.filter((p) => !p.id);
      const toDeleteIds = oldAnimationIds.filter(
        (id) => !animations.some((p) => p.id === id)
      );
      const toUpdateList = animations.filter((p) => p.id);
      const promises: Promise<any>[] = [];
      for (const animation of toAddList) {
        promises.push(
          ApiPostStoryGameDialogAnimation({
            dialogId,
            animationId: animation.animationId!,
            mediaInfo: animation.mediaInfo,
          })
        );
      }
      for (const animation of toUpdateList) {
        promises.push(
          ApiPutStoryGameDialogAnimation(animation.id!, {
            animationId: animation.animationId,
            mediaInfo: animation.mediaInfo,
          })
        );
      }
      for (const id of toDeleteIds) {
        promises.push(ApiDeleteStoryGameDialogAnimation(id));
      }
      await Promise.all(promises);
      const { item } = await ApiGetStoryGameDialogItem(dialogId);
      dialog.animationzz = item.animationzz;
    },
    async getOrFetchDialog(dialogId: number) {
      if (this.dialogMap[dialogId]) return this.dialogMap[dialogId];
      const { item } = await ApiGetStoryGameDialogItem(dialogId);
      this.dialogMap[dialogId] = item;
      return item;
    },
    // type = 'characterzz' | 'description'
    clickAutoGenerate(type: StoryGameParams, id?: number) {
      this.generateTarget = type;
      if (type === "characterzz") this.activeCharacterId = id!;
      this.generateChecked = ["type", type];
      this.showGenerate = true;
    },

    // game media
    // img upload
    clickOpenUploadModal(uploadType: number, mediaType = "img") {
      this.showImgUploadModal = true;
      this.uploadType = uploadType;
      this.mediaType = mediaType as "img" | "video";
    },

    deleteMedia(id: number) {
      showConfirmDialog({
        title: i18n.global.t("Message.delete"),
        message: i18n.global.t("ConfirmModal.sureDeleteText"),
        confirmButtonText: i18n.global.t("Buttons.confirm"),
        cancelButtonText: i18n.global.t("Buttons.cancel"),
      })
        .then(async () => {
          this.saveLoading = true;
          await ApiDeleteStoryGameMedia(id);
          this.saveLoading = false;
          const index = this.mediaList.indexOf(id);
          this.mediaList.splice(index, 1);
          delete this.mediaMap[id];
        })
        .catch(() => {});
    },

    clickSelectCharacterTone(id: number) {
      this.activeCharacterId = id;
      this.showSelectCharacterToneModal = true;
    },

    async onUpdateCharacterTone(toneId?: number) {
      const character = this.characterIdMap[this.activeCharacterId!];
      if (character.role === 3) {
        // 玩家
        this.form.player.mateToneId = toneId;
      } else if (character.role === 4) {
        // 旁白
        this.form.narrator.mateToneId = toneId;
      } else {
        const character = this.form.characterzz.find(
          (v) => v.id === this.activeCharacterId
        );
        character!.mateToneId = toneId;
      }
      character!.mateToneId = toneId;
      this.showSelectCharacterToneModal = false;
    },

    async fetchCharacterAllDialog() {
      return this.dialogList.map((id) => this.dialogMap[id]);
      // const { storyGameId, activeCharacterId, activeStoryNodeId } = this;
      // const _page = this.characterDialogList.length / 10 + 1;
      // if (!Number.isInteger(_page)) return true;
      // const { page } = await ApiGetStoryGameDialogPageByCharacterId(
      //   {
      //     storyGameId,
      //     storyNodeId: activeStoryNodeId,
      //     characterId: activeCharacterId,
      //   },
      //   { page: _page, perPage: 10 }
      // );
      // this.characterDialogList.push(...page.data);
      // return page.data.length === 10;
    },
  },
});
